import { Pause, Play, RotateCcw } from 'lucide-react';
import { useRef, useState } from 'react';
import { useReducedMotion, useVideoKeepAlive } from '../../motion';

const VIDEO_BASE = '/videos/draft/war-room';

// Overlay copy is timed to the three shots of the film (5s each):
// the truck down the table, Doster's empty chair, Kyle's extra day.
const BEATS = [
  { until: 5, top: 'THE WAR ROOM', bottom: 'SEVENTEEN ROUNDS · TEN WAR CHESTS' },
  { until: 10, top: 'DOSTER · RANKED FOURTH · 6.9%', bottom: 'HE ONLY ATTENDS THINGS THAT REQUIRE AN ACTUAL NUMBER' },
  { until: Infinity, top: 'TEICH GETS AN EXTRA DAY', bottom: 'MORE PREP HAS NOT ALWAYS LED TO SUCCESS' },
];

export default function WarRoomFilm() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [manualPause, setManualPause] = useState(false);
  const [beat, setBeat] = useState(0);
  const reduced = useReducedMotion();
  useVideoKeepAlive(videoRef, {
    enabled: !reduced && !manualPause && !ended,
    onPlayRejected: () => setPlaying(false),
  });

  const onTimeUpdate = () => {
    const t = videoRef.current?.currentTime ?? 0;
    const idx = BEATS.findIndex((b) => t < b.until);
    if (idx !== -1 && idx !== beat) setBeat(idx);
  };

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;

    if (ended) {
      video.currentTime = 0;
      setEnded(false);
      setManualPause(false);
      setBeat(0);
      video.play().catch(() => setPlaying(false));
      return;
    }

    if (video.paused) {
      setManualPause(false);
      video.play().catch(() => setPlaying(false));
    } else {
      setManualPause(true);
      video.pause();
    }
  };

  const buttonLabel = ended ? 'Replay the war room' : playing ? 'Pause the war room' : 'Play the war room';
  const ButtonIcon = ended ? RotateCcw : playing ? Pause : Play;
  const active = BEATS[beat];

  return (
    <section
      className="relative overflow-hidden border-b border-accent-gold/30 bg-black px-6 py-10 md:px-14 md:py-14"
      aria-labelledby="war-room-film-title"
      data-testid="war-room-film"
    >
      <div className="pointer-events-none absolute inset-0 opacity-20"
           style={{ backgroundImage: 'radial-gradient(circle at 28% 24%, rgba(251,191,36,.4), transparent 35%), linear-gradient(rgba(255,255,255,.035) 1px, transparent 1px)', backgroundSize: 'auto, 100% 4px' }}
           aria-hidden="true" />

      <div className="relative mx-auto grid max-w-6xl items-center gap-8 lg:grid-cols-[minmax(250px,0.72fr)_minmax(0,1.28fr)] lg:gap-12">
        <div>
          <div className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-accent-gold">
            Draft weekend · Dallas
          </div>
          <h2 id="war-room-film-title" className="mt-3 font-display text-4xl font-black uppercase leading-[0.9] md:text-5xl">
            The war room.
            <span className="block text-accent-gold">Doors open Saturday.</span>
          </h2>
          <p className="mt-5 max-w-[36ch] font-body text-base italic leading-relaxed text-ink-secondary md:text-lg">
            Thirteen seasons of receipts, seated at one table. Binders out. Boards set. Ten franchises come ready to battle.
          </p>
          <div className="mt-6 inline-flex border border-accent-gold/50 bg-accent-gold/10 px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-accent-gold">
            Live draft · Saturday, Aug 29 · 2:00 PM PDT
          </div>
        </div>

        <div className="relative aspect-video overflow-hidden border border-white/15 bg-bg-deep shadow-[0_24px_70px_rgba(0,0,0,0.55)]">
          <video
            ref={videoRef}
            className="h-full w-full object-cover"
            muted
            playsInline
            preload="auto"
            poster={`${VIDEO_BASE}-poster.webp`}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => { setPlaying(false); setEnded(true); }}
            onTimeUpdate={onTimeUpdate}
            data-testid="war-room-film-video"
          >
            <source src={`${VIDEO_BASE}-480p.webm`} type="video/webm" />
            <source src={`${VIDEO_BASE}-480p.mp4`} type="video/mp4" />
          </video>

          <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-between bg-gradient-to-b from-black/75 to-transparent px-3 pb-7 pt-3 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-white/75" aria-hidden="true">
            <span>Bruins 09 · The War Room</span>
            <span>00:15.1</span>
          </div>

          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 bg-gradient-to-t from-black/85 via-black/35 to-transparent px-3 pb-3 pt-12" aria-hidden="true">
            <div className="hidden font-display text-[11px] font-black uppercase tracking-[0.16em] text-white sm:block">
              {active.top}
            </div>
            <div className="hidden max-w-[46ch] text-right font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-accent-gold sm:block">
              {active.bottom}
            </div>
          </div>

          {ended && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/62 px-6 text-center">
              <div>
                <div className="font-mono text-[9px] font-bold uppercase tracking-[0.24em] text-accent-gold">
                  The room is set
                </div>
                <div className="mt-2 font-display text-2xl font-black uppercase leading-none text-white md:text-3xl">
                  Saturday · Aug 29 · 2:00 PM PDT
                </div>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={togglePlayback}
            className="absolute bottom-3 left-1/2 z-10 inline-flex min-h-11 -translate-x-1/2 items-center gap-2 border border-white/40 bg-black/85 px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:border-accent-gold hover:text-accent-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-gold md:bottom-4"
            aria-label={buttonLabel}
          >
            <ButtonIcon size={14} aria-hidden="true" />
            {ended ? 'Run it back' : playing ? 'Hold tape' : 'Roll tape'}
          </button>
        </div>
      </div>
    </section>
  );
}
